import Button from '~components/ui/button'
import ProgressBar from '~components/ui/progress-bar'
import TextButton from '~components/ui/button/text-button'

import { cn } from '~utils/cn'
import HeaderBlock from '../../../../components/general/header-block'

interface ConsultationLayoutProps {
  children?: React.ReactNode
  title?: string
  // 제목 요소 id (본문에서 aria-labelledby 로 가리킬 때)
  titleId?: string
  description?: string
  // description 아래 한 줄 안내 (예: "None of these? Just tap Next.")
  hint?: string
  required?: boolean
  nextButtonDisabled?: boolean
  nextButtonText?: string
  onNextPress?: () => void
  onSkipPress?: () => void
  // "I don't know" 버튼 문구 (문진 v2 는 질문지의 unknown_label)
  skipLabel?: string
  // 이미 "I don't know" 로 답한 질문: 버튼을 고른 상태(테두리 secondary)로 그린다
  skipSelected?: boolean
  // 진행 바: 현재 화면 번호(1부터)와 전체 화면 수. 없으면 그리지 않는다
  progress?: { current: number; total: number } | null
  // Next 버튼 바로 위에 붙는 내용 (예: 주요 증상 화면의 고른 증상 설명)
  footer?: React.ReactNode
}

const ConsultationLayout = ({
  children,
  title,
  titleId,
  description,
  hint,
  required,
  nextButtonText,
  nextButtonDisabled,
  onNextPress,
  onSkipPress,
  skipLabel = `I don't know`,
  skipSelected = false,
  progress,
  footer,
}: ConsultationLayoutProps) => {
  return (
    <main className="flex flex-col items-center h-full max-h-dynamic-screen w-full overflow-hidden">
      {/* 헤더 여백과 진행 바는 스크롤 영역 밖에 둬서 위에 고정한다 */}
      <HeaderBlock />
      {progress && (
        <div
          className="w-full px-5 pt-2 shrink-0"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={progress.total}
          aria-valuenow={Math.min(progress.current, progress.total)}
        >
          <ProgressBar variant="continuous" total={progress.total} currentStep={progress.current} />
        </div>
      )}
      <section className="flex-1 overflow-y-auto w-full overflow-x-hidden">
        {(title || description) && (
          <section
            className={cn('flex flex-col py-6 px-5 w-full', title && description && 'flex-gap-y-4')}
          >
            {title && (
              <h2
                id={titleId}
                className="text-gray-900 text-headline-5 whitespace-pre-line md:whitespace-nowrap dark:text-white"
              >
                {title}
              </h2>
            )}
            {description && (
              <div className="flex flex-col flex-gap-y-1">
                <p className="text-gray-500 text-body-2">{description}</p>
                {hint && <p className="text-gray-500 text-body-2">{hint}</p>}
              </div>
            )}
          </section>
        )}
        <section className="w-full px-5 flex-1">{children}</section>
      </section>

      <section className="sticky bottom-0 w-full flex flex-col flex-gap-y-2 p-5 bg-gradient-to-t from-background dark:from-background dark:to-background/0 from-90% to-background/0 justify-end pt-10 shrink-0">
        {footer}
        <Button
          className="w-full dark:disabled:bg-gray-900 dark:disabled:text-gray-700 shrink-0"
          disabled={nextButtonDisabled}
          onClick={onNextPress}
        >
          {nextButtonText || 'Next'}
        </Button>
        {!required &&
          (skipSelected ? (
            // 고른 상태: 디자인 시스템 secondary(테두리) 버튼. 다시 눌러도 '모름' 그대로 다음으로 간다
            <Button
              className="w-full shrink-0 dark:border-primary-400 dark:text-primary-400"
              priority="secondary"
              aria-pressed={true}
              onClick={onSkipPress}
            >
              {skipLabel}
            </Button>
          ) : (
            <TextButton
              className="w-full dark:text-gray-500 !bg-transparent"
              priority="tertiary"
              aria-pressed={false}
              onClick={onSkipPress}
            >
              {skipLabel}
            </TextButton>
          ))}
      </section>
    </main>
  )
}

export default ConsultationLayout
